import { useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NoteEditor } from "@/components/admin/NoteEditor";

const INTERVALS = [["week", "Hver uke"], ["biweek", "Hver 14. dag"], ["month", "Hver måned"]];

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm">{label}</Label>
    {children}
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
  </div>
);

const Recipients = ({ list, onChange }) => {
  const [v, setV] = useState("");
  const add = (e) => {
    e.preventDefault();
    const email = v.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error("Skriv inn en gyldig e-postadresse");
    if (list.includes(email)) return toast.error("Adressen er allerede lagt til");
    onChange([...list, email]);
    setV("");
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2" data-testid="pax-recipients">
        {list.length === 0 && <span className="text-sm text-slate-500">Ingen mottakere ennå</span>}
        {list.map((r) => (
          <span key={r} className="inline-flex items-center gap-1 rounded-full bg-sky-50 border border-sky-200 text-sky-900 text-sm pl-3 pr-1 py-0.5">
            {r}<button type="button" onClick={() => onChange(list.filter((x) => x !== r))} className="p-1 rounded-full hover:bg-sky-100" aria-label={`Fjern ${r}`} data-testid={`pax-recipient-remove-${r}`}><X className="w-3.5 h-3.5" /></button>
          </span>
        ))}
      </div>
      <form onSubmit={add} className="flex gap-2">
        <Input type="email" placeholder="navn@havn.no" value={v} onChange={(e) => setV(e.target.value)} data-testid="pax-recipient-input" />
        <Button type="submit" variant="outline" data-testid="pax-recipient-add"><Plus className="w-4 h-4 mr-1" />Legg til</Button>
      </form>
    </div>
  );
};

const ModeCard = ({ active, title, hint, onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`text-left rounded-md border px-4 py-3 transition-colors ${active ? "border-sky-600 bg-sky-50 ring-1 ring-sky-600" : "border-slate-200 bg-white hover:border-sky-300"}`}>
    <div className="text-sm font-medium text-slate-900">{title}</div>
    <div className="text-xs text-slate-500">{hint}</div>
  </button>
);

export const PaxSettings = ({ pax, onChange, cid }) => {
  const set = (patch, delay = 500) => onChange({ ...pax, ...patch }, delay);
  const num = (k) => (e) => set({ [k]: Math.max(0, Number(String(e.target.value).replace(",", ".")) || 0) });
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-6 space-y-6" data-testid="pax-settings">
      <label className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-4 py-3 cursor-pointer">
        <div>
          <div className="text-sm font-medium text-slate-900">Skjemaet er aktivt</div>
          <div className="text-xs text-slate-500">Når det er av, viser skjemaet «ikke tilgjengelig» på nettsiden</div>
        </div>
        <Switch checked={!!pax.enabled} onCheckedChange={(v) => set({ enabled: v }, 0)} data-testid="pax-enabled-switch" />
      </label>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Havnekontorets navn"><Input value={pax.office_name} onChange={(e) => set({ office_name: e.target.value })} data-testid="pax-office-input" /></Field>
        <Field label="Innsendingsintervall">
          <Select value={pax.interval} onValueChange={(v) => set({ interval: v }, 0)}>
            <SelectTrigger data-testid="pax-interval-select"><SelectValue /></SelectTrigger>
            <SelectContent>{INTERVALS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Pris per passasjer (kr)"><Input inputMode="decimal" defaultValue={pax.price} onBlur={num("price")} data-testid="pax-price-input" /></Field>
        <Field label="Gebyr ved for sen innsending (kr)"><Input inputMode="decimal" defaultValue={pax.late_fee} onBlur={num("late_fee")} data-testid="pax-fee-input" /></Field>
        <Field label="Frist: dager etter periodens slutt" hint={pax.interval === "month" ? "F.eks. 5 = den 5. i måneden etter" : "1 = mandagen etter perioden"}>
          <Input type="number" min={0} max={28} value={pax.deadline_days} onChange={(e) => set({ deadline_days: Math.min(28, Math.max(0, parseInt(e.target.value || "0", 10))) })} data-testid="pax-deadline-days" />
        </Field>
        <Field label="Frist: klokkeslett (TT:MM)"><Input inputMode="numeric" placeholder="12:00" defaultValue={pax.deadline_time} onBlur={(e) => { const v = e.target.value.trim().replace(".", ":"); if (/^([01]\d|2[0-3]):[0-5]\d$/.test(v)) set({ deadline_time: v }, 0); else { toast.error("Skriv klokkeslett som TT:MM, f.eks. 12:00"); e.target.value = pax.deadline_time; } }} data-testid="pax-deadline-time" /></Field>
      </div>
      <Field label="Tekst øverst i skjemaet" hint="Bruk {havnekontor}, {pris}, {gebyr} og {frist}. De byttes automatisk ut med verdiene over.">
        <NoteEditor key={`pax-${cid}`} html={pax.intro_html} hideToggle onHtml={(h) => set({ intro_html: h }, 800)} testId="pax-intro-editor" />
      </Field>
      <Field label="Rapport-mottakere (e-post)"><Recipients list={pax.recipients || []} onChange={(l) => set({ recipients: l }, 0)} /></Field>
      <Field label="Når skal mottakerne få e-post?">
        <div className="grid sm:grid-cols-2 gap-3">
          <ModeCard active={pax.email_mode === "each"} title="Ved hver innsending" hint="Én e-post med PDF per rapport" onClick={() => set({ email_mode: "each" }, 0)} testId="pax-mode-each" />
          <ModeCard active={pax.email_mode === "daily"} title="Samlet kl. 15:00" hint="Én e-post per dag med alle dagens rapporter" onClick={() => set({ email_mode: "daily" }, 0)} testId="pax-mode-daily" />
        </div>
      </Field>
      <label className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-4 py-3 cursor-pointer" data-testid="pax-reminder-row">
        <div>
          <div className="text-sm font-medium text-slate-900">Send påminnelse til rederier som ikke har levert</div>
          <div className="text-xs text-slate-500">Sendes automatisk mandag kl. 08:00 til rederiets e-post, kun hvis rapporten for forrige periode mangler.</div>
        </div>
        <Switch checked={pax.reminder_enabled !== false} onCheckedChange={(v) => set({ reminder_enabled: v }, 0)} data-testid="pax-reminder-switch" />
      </label>
    </div>
  );
};
